import apiClient from './client';
import type {
  Session,
  CoachHuddle,
  FinalizationStatus,
  FinalizeSessionsRequest,
  FinalizeSessionsResponse,
  SessionRsvpDetails,
  SessionRsvpSummary,
  SessionDisplayStatus,
  MarkAttendanceRequest,
  MarkAttendanceResponse,
  SessionAttendanceDetail,
  ParticipantAttendanceInput,
  SessionNotesResponse,
  SessionNoteWithAuthor,
  SessionDetailStats,
  OneOnOneParticipant,
  PaginatedResponse,
  EligibleOneOnOneCoach,
  UnenrolledOneOnOneParticipant,
} from '../../types';

/** MED-1350: the 1:1 list page. pagination adds the cohort-wide count of
 * participants with a paid 1:1 row, and of participants with a live check-in
 * row (a booked check-in stays after both toggles go off). */
export interface OneOnOneParticipantsPage extends PaginatedResponse<OneOnOneParticipant> {
  pagination: PaginatedResponse<OneOnOneParticipant>['pagination'] & {
    paid_participant_total?: number;
    checkin_participant_total?: number;
  };
}

// Session with group details
export interface SessionWithDetails extends Session {
  group_name?: string;
  cohort_name?: string;
  time_slot_identifier?: string;
  topic_name?: string;
  display_status?: SessionDisplayStatus;
  avg_engagement?: number | null;
  // MED-1350: the session's own coach (sessions.coach_id). The name and avatar
  // are sent by getById only.
  coach_id?: string | null;
  session_coach_name?: string | null;
  session_coach_avatar_url?: string | null;
  // MED-1350: the session coach's profile meeting link. Sent by getById only.
  session_coach_meeting_link?: string | null;
}

// Session with RSVP summary for list display
export interface SessionWithDetailsAndRsvp extends SessionWithDetails {
  rsvp_summary?: SessionRsvpSummary;
  attendance_total?: number;
  attendance_attended?: number;
  // MED-1266: recorded outcomes + pending = coverage population for the per-session rate
  attendance_population?: number;
}

// Session list response
export interface SessionListResponse {
  sessions: SessionWithDetails[];
  total: number;
}

// Session filter options
export interface SessionFilterOptions {
  groupId?: string;
  status?: 'scheduled' | 'completed' | 'cancelled';
}

export const sessionsApi = {
  /**
   * Get all sessions for a cohort
   */
  list: async (cohortId: string, filters?: SessionFilterOptions): Promise<SessionWithDetails[]> => {
    const params = new URLSearchParams();
    if (filters?.groupId) params.append('groupId', filters.groupId);
    if (filters?.status) params.append('status', filters.status);

    const queryString = params.toString();
    const url = `/api/cohorts/${cohortId}/sessions${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<SessionWithDetails[]>(url);
  },

  /**
   * Get a single session by ID
   */
  getById: async (cohortId: string, sessionId: string): Promise<SessionWithDetails> => {
    return apiClient.get<SessionWithDetails>(`/api/cohorts/${cohortId}/sessions/${sessionId}`);
  },

  /**
   * Get sessions for a specific group
   * Uses the list endpoint with groupId filter (the nested route doesn't exist)
   */
  listByGroup: async (cohortId: string, groupId: string): Promise<SessionWithDetails[]> => {
    return apiClient.get<SessionWithDetails[]>(
      `/api/cohorts/${cohortId}/sessions?groupId=${groupId}`,
    );
  },

  /**
   * Update a session
   */
  update: async (
    cohortId: string,
    sessionId: string,
    data: Partial<Session>,
  ): Promise<SessionWithDetails> => {
    return apiClient.patch<SessionWithDetails>(`/api/cohorts/${cohortId}/sessions/${sessionId}`, data);
  },

  /**
   * Generate sessions for groups in a cohort
   */
  generateForCohort: async (cohortId: string): Promise<{
    totalGroups: number;
    totalSessions: number;
    errors: Array<{ groupId: string; error: string }>;
  }> => {
    return apiClient.post(`/api/cohorts/${cohortId}/sessions/generate`, {});
  },

  // ============================================================================
  // 1:1 Coaching Endpoints (E18-1)
  // ============================================================================

  /**
   * Get all participants enrolled in 1:1 coaching with their session slots.
   *
   * MED-987: pass `coachId` to restrict the result (and its count) to a single
   * coach's assigned participants server-side. The coach view supplies its own
   * id so it no longer relies on a client-side filter over a capped page; the
   * admin view omits it to see every participant.
   */
  /** MED-1350: pagination also carries paid_participant_total. */
  listOneOnOneParticipants: async (
    cohortId: string,
    params?: { page?: number; limit?: number; coachId?: string },
  ): Promise<OneOnOneParticipantsPage> => {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.coachId) qs.set('coachId', params.coachId);
    const query = qs.toString();
    return apiClient.getPaginated<OneOnOneParticipant>(
      `/api/cohorts/${cohortId}/sessions/one-on-one${query ? `?${query}` : ''}`,
    ) as Promise<OneOnOneParticipantsPage>;
  },

  /**
   * Generate empty 1:1 session slots for all participants who don't have them yet
   */
  generateOneOnOneSlots: async (cohortId: string): Promise<{ created_count: number; session_count_per_participant: number }> => {
    return apiClient.post(`/api/cohorts/${cohortId}/sessions/one-on-one/generate`, {});
  },

  // ============================================================================
  // 1:1 Coaching Endpoints (E18-2) — Coach Assignment & Participant Enrollment
  // ============================================================================

  /**
   * Get coaches eligible for 1:1 assignment (confirmed availability for one_on_one modality)
   */
  listEligibleOneOnOneCoaches: async (cohortId: string): Promise<EligibleOneOnOneCoach[]> => {
    return apiClient.get<EligibleOneOnOneCoach[]>(
      `/api/cohorts/${cohortId}/sessions/one-on-one/eligible-coaches`,
    );
  },

  /**
   * Get participants not yet enrolled in 1:1 coaching
   */
  listUnenrolledOneOnOneParticipants: async (cohortId: string): Promise<UnenrolledOneOnOneParticipant[]> => {
    return apiClient.get<UnenrolledOneOnOneParticipant[]>(
      `/api/cohorts/${cohortId}/sessions/one-on-one/unenrolled-participants`,
    );
  },

  /**
   * Add participants to 1:1 coaching — creates session slots and auto-assigns group coach
   */
  addOneOnOneParticipants: async (
    cohortId: string,
    participantUserIds: string[],
  ): Promise<{ created_count: number; session_count_per_participant: number; participant_count: number }> => {
    return apiClient.post(
      `/api/cohorts/${cohortId}/sessions/one-on-one/participants`,
      { participant_user_ids: participantUserIds },
    );
  },

  /**
   * Assign or change coach for a participant's unscheduled 1:1 sessions
   */
  assignOneOnOneCoach: async (
    cohortId: string,
    participantUserId: string,
    coachUserId: string | null,
  ): Promise<{ sessions_updated: number }> => {
    return apiClient.put(
      `/api/cohorts/${cohortId}/sessions/one-on-one/participants/${participantUserId}/coach`,
      { coach_user_id: coachUserId },
    );
  },

  // ============================================================================
  // Finalization & RSVP Endpoints (MED-173)
  // ============================================================================

  /**
   * Get finalization status for a cohort
   * Returns readiness info and lists of finalized/unfinalized groups
   */
  getFinalizationStatus: async (cohortId: string): Promise<FinalizationStatus> => {
    return apiClient.get<FinalizationStatus>(
      `/api/cohorts/${cohortId}/sessions/finalization-status`,
    );
  },

  /**
   * Finalize sessions and send calendar invites
   * Marks sessions as finalized, sets video link, and creates attendance records
   */
  finalizeAndSendInvites: async (
    cohortId: string,
    data: FinalizeSessionsRequest,
  ): Promise<FinalizeSessionsResponse> => {
    return apiClient.post<FinalizeSessionsResponse>(
      `/api/cohorts/${cohortId}/sessions/finalize`,
      data,
    );
  },

  /**
   * Resend invites for already finalized sessions
   */
  resendInvites: async (
    cohortId: string,
    groupIds: string[],
  ): Promise<{
    invites_sent: number;
    sessions_updated: number;
    errors?: string[];
    // MED-1300: send-outcome fields (see FinalizeSessionsResponse).
    skipped_participants?: import('../../types').SkippedInviteParticipant[];
    email_fallback_sessions?: number;
    sessions_processed?: number;
    calendar_failures?: import('../../types').InviteCalendarFailure[];
  }> => {
    return apiClient.post(`/api/cohorts/${cohortId}/sessions/resend-invites`, {
      group_ids: groupIds,
    });
  },

  /**
   * List sessions with RSVP summaries for a cohort
   */
  listWithRsvp: async (cohortId: string): Promise<SessionWithDetailsAndRsvp[]> => {
    return apiClient.get<SessionWithDetailsAndRsvp[]>(
      `/api/cohorts/${cohortId}/sessions/with-rsvp`,
    );
  },

  /**
   * Get RSVP details for a single session
   */
  getSessionRsvp: async (cohortId: string, sessionId: string): Promise<SessionRsvpDetails> => {
    return apiClient.get<SessionRsvpDetails>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/rsvp`,
    );
  },

  /**
   * Update RSVP status for an attendee (for webhook or manual update)
   */
  updateRsvp: async (
    cohortId: string,
    sessionId: string,
    userId: string,
    status: 'pending' | 'accepted' | 'declined' | 'tentative',
  ): Promise<{ id: string; rsvp_status: string }> => {
    return apiClient.put(`/api/cohorts/${cohortId}/sessions/${sessionId}/rsvp`, {
      user_id: userId,
      status,
    });
  },

  /**
   * Get session stats (counts by status)
   */
  getStats: async (cohortId: string): Promise<Record<string, number>> => {
    return apiClient.get<Record<string, number>>(`/api/cohorts/${cohortId}/sessions/stats`);
  },

  /**
   * Get detail stats for a single session (MED-89)
   * Used in session detail modal header
   */
  getSessionDetailStats: async (
    cohortId: string,
    sessionId: string,
  ): Promise<SessionDetailStats> => {
    return apiClient.get<SessionDetailStats>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/detail-stats`,
    );
  },

  // ============================================================================
  // Attendance Endpoints (MED-93 E8-11)
  // ============================================================================

  /**
   * Get sessions with display status (E8-13)
   * Display status is computed: SCHEDULED (future), AWAITING_ATTENDANCE (past, not marked), COMPLETED (marked)
   */
  listWithDisplayStatus: async (cohortId: string): Promise<SessionWithDetails[]> => {
    return apiClient.get<SessionWithDetails[]>(
      `/api/cohorts/${cohortId}/sessions/with-display-status`,
    );
  },

  /**
   * Get attendance details for a session (for CM view/edit)
   * Also used for marking mode as it returns participants with their current status
   */
  getAttendance: async (
    cohortId: string,
    sessionId: string,
  ): Promise<SessionAttendanceDetail> => {
    return apiClient.get<SessionAttendanceDetail>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/attendance`,
    );
  },

  /**
   * Mark attendance for a session (initial submission)
   */
  markAttendance: async (
    cohortId: string,
    sessionId: string,
    data: MarkAttendanceRequest & { engagement_ratings?: { participant_id: string; rating: number }[] },
  ): Promise<MarkAttendanceResponse> => {
    return apiClient.post<MarkAttendanceResponse>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/attendance`,
      data,
    );
  },

  /**
   * Update attendance for a session (CM edit mode)
   * Optionally send surveys to participants whose status changed to attended/partial
   */
  updateAttendance: async (
    cohortId: string,
    sessionId: string,
    attendance: ParticipantAttendanceInput[],
    options?: { send_surveys?: boolean; changed_user_ids?: string[]; engagement_ratings?: { participant_id: string; rating: number }[] },
  ): Promise<{ updated_count: number }> => {
    return apiClient.put<{ updated_count: number }>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/attendance`,
      {
        attendance,
        send_surveys: options?.send_surveys,
        changed_user_ids: options?.changed_user_ids,
        engagement_ratings: options?.engagement_ratings,
      },
    );
  },

  /**
   * MED-1360: clear a wrong attendance mark on a 1:1 or check-in, back to
   * scheduled. The API refuses with a 409 when a payment for the session is
   * already in Bill.com.
   */
  clearAttendance: async (
    cohortId: string,
    sessionId: string,
  ): Promise<{ session_id: string; status: string; cleared_count: number }> => {
    return apiClient.delete<{ session_id: string; status: string; cleared_count: number }>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/attendance`,
    );
  },

  // ============================================================================
  // Session Notes Endpoints (MED-93 E8-12)
  // ============================================================================

  /**
   * Get notes for a session
   */
  getNotes: async (cohortId: string, sessionId: string): Promise<SessionNotesResponse> => {
    return apiClient.get<SessionNotesResponse>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/notes`,
    );
  },

  /**
   * Create a note for a session
   */
  createNote: async (
    cohortId: string,
    sessionId: string,
    content: string,
  ): Promise<SessionNoteWithAuthor> => {
    return apiClient.post<SessionNoteWithAuthor>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/notes`,
      { content },
    );
  },
  // ============================================================================
  // Coach Feedback & Concern Flags Endpoints (MED-91 E8-4, E8-5)
  // ============================================================================

  /**
   * Get coach feedback for a session (CM read-only view)
   */
  getCoachFeedback: async (
    cohortId: string,
    sessionId: string,
  ): Promise<{
    feedback: import('./coachPortal').CoachSessionFeedbackWithAuthor[];
    concernFlags: import('./coachPortal').ConcernFlagWithDetails[];
    engagementRatings: import('./coachPortal').ParticipantEngagementRating[];
  }> => {
    return apiClient.get(`/api/cohorts/${cohortId}/sessions/${sessionId}/coach-feedback`);
  },

  /**
   * Get concern flags for a session
   */
  getConcernFlags: async (
    cohortId: string,
    sessionId: string,
  ): Promise<import('./coachPortal').ConcernFlagWithDetails[]> => {
    return apiClient.get(`/api/cohorts/${cohortId}/sessions/${sessionId}/concern-flags`);
  },

  /**
   * Mark a concern flag as addressed
   */
  markConcernFlagAddressed: async (
    cohortId: string,
    sessionId: string,
    flagId: string,
    resolutionNotes?: string,
  ): Promise<import('./coachPortal').ConcernFlagWithDetails> => {
    return apiClient.patch(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/concern-flags/${flagId}`,
      { resolution_notes: resolutionNotes },
    );
  },

  /**
   * Get concern flags for a participant
   */
  getParticipantConcernFlags: async (
    cohortId: string,
    participantId: string,
  ): Promise<import('./coachPortal').ConcernFlagWithDetails[]> => {
    return apiClient.get(
      `/api/cohorts/${cohortId}/participants/${participantId}/concern-flags`,
    );
  },

  // ============================================================================
  // Huddle Endpoints - Admin/CM View (MED-268)
  // ============================================================================

  /**
   * Get all huddles for a cohort (admin/CM read-only view)
   */
  getCohortHuddles: async (cohortId: string): Promise<CoachHuddle[]> => {
    return apiClient.get<CoachHuddle[]>(`/api/cohorts/${cohortId}/huddles`);
  },

  // ============================================================================
  // Session Leader Designation (MED-812 / E4.0a)
  // ============================================================================

  /**
   * Designate or reassign the peer Session Leader for a session.
   *
   * Gated server-side by the `live_session_delivery` feature flag (404 when
   * off) and by the modality being group_coaching + peer_led (422
   * `not_peer_led`). After `scheduled_start` the API rejects with 422
   * `session_already_started` — callers should hide the UI affordance once
   * the boundary is crossed rather than relying on the server message.
   */
  assignSessionLeader: async (
    cohortId: string,
    sessionId: string,
    sessionLeaderId: string,
  ): Promise<SessionWithDetails> => {
    return apiClient.put<SessionWithDetails>(
      `/api/cohorts/${cohortId}/sessions/${sessionId}/leader`,
      { session_leader_id: sessionLeaderId },
    );
  },
};

export default sessionsApi;
